import {
  Box,
  FormControlLabel,
  Radio,
  RadioGroup,
  Typography,
} from "@mui/material";
import { T } from "react-translator-component";
import "react-multi-carousel/lib/styles.css";
import { useState, useRef } from "react";
import { useSelector } from "react-redux";
import Carousel from "react-multi-carousel";
import "../../../App.css";
import { colors } from "../../../common/colors";
import Separator from "../../../components/Separator";
import ButtonOutlinedCentered from "../../../components/ButtonOutlined";
import { setItem } from "../../redux/createOpinionReducer";
import { useAppDispatch } from "../../../redux/store";
import { PrivateUser } from "../../util/createPost";
import { UserEntity } from "../../util/editors";
import AddUserModal from "../editors/AddUserModal";
import SelectedUser from "../editors/SelectedUser";

interface Props {
  isEdit: boolean;
  handleAddPrivateUser: (user: UserEntity) => void;
  handleRemovePrivateUser: (user: UserEntity) => void;
}

export default function SelectPostType({ isEdit, handleAddPrivateUser, handleRemovePrivateUser }: Props) {
  const dispatch = useAppDispatch();
  const post = useSelector((state: any) => state.createOpinion.values);
  const isPrivate = post.isPrivate;
  const privateUsers = post.privateUsers.filter((privateUser: PrivateUser) => privateUser._destroy !== 1);
  const [isUserModelVisible, setIsUserModelVisible] = useState(false);

  const carouselRef = useRef<Carousel>(null);

  const handleAddUserClick = () => {
    setIsUserModelVisible(true);
  };

  const handleCloseModal = () => {
    setIsUserModelVisible(false);
  };

  const handleThreadType = (value: string) => {
    dispatch(setItem({key: 'isPrivate', value: value === "true"}))
  };

  const handleDelete = (user: UserEntity) => {
    if (carouselRef.current) {
      carouselRef.current.goToSlide(0);
    }
    handleRemovePrivateUser(user)
  }

  return (
    <div style={{ marginTop: 20 }}>
      <div style={{ padding: 10 }}>
        <Typography
          sx={{ fontSize: 16, fontWeight: "600", flex: 1, textAlign: "center" }}
        >
          {T("select_post_type")}
        </Typography>
        <Separator color={colors.farm_green200} />
        <RadioGroup
          style={{ pointerEvents: isEdit ? "none" : "auto" }}
          aria-labelledby="radio-buttons-group-label"
          defaultValue="0"
          name="radio-buttons-group"
          value={isPrivate}
          onChange={(e) => handleThreadType(e.target.value)}
        >
          <FormControlLabel
            value={false}
            disabled={isEdit && isPrivate}
            control={
              <Radio
                sx={{
                  "&, &.Mui-checked": {
                    color: colors.farm_blue200,
                  },
                }}
              />
            }
            label={
              <div>
                <Typography
                  sx={{ fontWeight: "600", mv: 12 }}
                  variant="body2"
                  color={isEdit && isPrivate ? "text.disabled" : "textPrimary"}
                >
                  {T("post_type_open")}
                </Typography>

                <Typography variant="body2" color="textSecondary">
                  {T("open_post_description")}
                </Typography>
              </div>
            }
            labelPlacement="end"
            sx={{
              paddingTop: 2,
              alignItems: "flex-start",
            }}
          />
          <FormControlLabel
            value={true}
            disabled={isEdit && !isPrivate}
            control={
              <Radio
                sx={{
                  "&, &.Mui-checked": {
                    color: colors.farm_blue200,
                  },
                }}
              />
            }
            label={
              <div>
                <Typography
                  sx={{ fontWeight: "600", mv: 12 }}
                  variant="body2"
                  color={isEdit && !isPrivate ? "text.disabled" : "textPrimary"}
                >
                  {T("mng_private_post")}
                </Typography>

                <Typography variant="body2" color="textSecondary">
                  {T("private_post_description")}
                </Typography>
              </div>
            }
            labelPlacement="end"
            sx={{
              paddingTop: 6,
              alignItems: "flex-start",
            }}
          />
        </RadioGroup>
      </div>

      {isPrivate && (
        <div
          style={{
            marginBottom: 8,
            padding: 10,
            backgroundColor: colors.farm_gray10,
          }}
        >
          <Box display="flex" alignItems="center">
            <Typography variant="body2" color="textPrimary">
              {T("post_private_users")}
            </Typography>
            <Typography
              variant="body2"
              style={{
                backgroundColor: colors.farm_green500,
                borderRadius: "4px",
                color: "white",
                padding: "2px 8px",
                marginLeft: "8px",
                marginBottom: "2px",
              }}
            >
              {privateUsers.length}
            </Typography>
          </Box>

          <Separator color={colors.farm_green200} />

          <Carousel
            responsive={{
              superLargeDesktop: {
                breakpoint: { max: 4000, min: 1024 },
                items: 5,
              },
              desktop: { breakpoint: { max: 1024, min: 768 }, items: 3 },
              tablet: { breakpoint: { max: 768, min: 464 }, items: 2 },

              mobile: {
                breakpoint: { max: 464, min: 0 },
                items: 2,
              },
            }}
            swipeable={true}
            draggable={true}
            showDots={false}
            arrows={true}
            infinite={false}
            keyBoardControl={true}
            itemClass="carousel-item-padding"
            ref={carouselRef}
          >
            {privateUsers.slice().reverse().map((privateUser: PrivateUser) => (
              <SelectedUser
                isEdit={isEdit}
                key={privateUser.userId}
                user={privateUser}
                onDelete={() => handleRemovePrivateUser(privateUser)}
              />
            ))}
          </Carousel>

          <ButtonOutlinedCentered
            title={T("add_users")}
            onClick={handleAddUserClick}
          />

          {isUserModelVisible && (
            <AddUserModal
              label={T("mng_can_view_post_user")}
              initialUsers={privateUsers}
              includeNavigator={true}
              onAdd={handleAddPrivateUser}
              onDelete={handleDelete}
              onClose={() => handleCloseModal()}
            />
          )}
        </div>
      )}
    </div>
  );
}
